import { digger } from '@cbre/digger';
import { areaUomAbbrev, concatValuesBy, defaultTxt, formatNumber, UserSettings } from '@miq/utils';
import moment from 'moment';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../models/report.model';
import { TemplateFieldsDisplayFn } from '../../../models/template.model';
import { sortAttributesByFieldId, sortAttributesFields } from '../utils/argument-sorting';
import {
  COMMON_UOM_MAP,
  displayFnWrapper,
  formatByUom,
  formatQuotingPrice,
  getStatus,
  getUsageSubType,
  mapAndFormatDynamicValue,
} from '../utils/dynamic-data-retrievers';
import { getYieldPercentageByYieldType, YIELD_DETAILS } from './common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  27: {
    // 27 - Address
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
    routeFn: ({ data: entityRecord }: ReportRecord) => {
      return ['/records', 'property', digger(entityRecord, 'property.id'), 'details'];
    },
  },
  349: {
    // 349 - Address (word report)
    displayFn: (record: ReportRecord, options, ...args) => {
      const { displayFnArgumentsOrder } = options;
      const finalArgs = sortAttributesFields(args, displayFnArgumentsOrder);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
  },
  49: {
    // 49 - Property Name
    displayFn: (record: ReportRecord, _options, arg1, arg2) => {
      return concatValuesBy(defaultTxt, mapAndFormatDynamicValue(record, arg1), mapAndFormatDynamicValue(record, arg2));
    },
  },
  25: {
    // 25 - Header
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 25);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
  },
  16: { displayFn: displayFnWrapper }, // 16 - Property Description
  17: {
    // 17- status
    displayFn: (record: ReportRecord, _options, statusType) => {
      return statusType.lovValues.pipe(
        map(lovs => {
          const result = getStatus(record, statusType, lovs);
          return result ? result : defaultTxt;
        })
      );
    },
  },
  2: {
    // 2 - Property Type
    displayFn: (record: ReportRecord, _, subType) => {
      return subType.lovValues.pipe(
        map(lovs => {
          const result = getUsageSubType(record, subType, lovs);
          return result ? result : '-';
        })
      );
    },
  },
  3: { displayFn: displayFnWrapper }, // 3 - Tenure
  319: { displayFn: displayFnWrapper }, // 319 - Tenant comment

  4: {
    // 4 - Area
    displayFn: (record: ReportRecord, _, area) => {
      const dynamicValue = mapAndFormatDynamicValue(record, area);
      return formatByUom(Array.isArray(dynamicValue) ? dynamicValue[0] : dynamicValue);
    },
  },
  5: { displayFn: displayFnWrapper }, // 5 - Tenant
  288: {
    displayFn: (record: ReportRecord, _, wault, wautc) => {
      const waultValue = formatNumber(mapAndFormatDynamicValue(record, wault), undefined, '.2-2');
      const wautcvalue = formatNumber(mapAndFormatDynamicValue(record, wautc), undefined, '.2-2');
      return `${waultValue} (${wautcvalue})`;
    },
  }, // 288 - WAULT (WAUTC) (years)
  320: {
    // 320 - Current Rent Per Annum
    displayFn: (record: ReportRecord, options, currentRentPerAnnum, askingRent) => {
      currentRentPerAnnum = { ...currentRentPerAnnum, dataFormat: '.0-0' };
      askingRent = { ...askingRent, dataFormat: '.0-0' };
      const currentRentValue = mapAndFormatDynamicValue(record, currentRentPerAnnum);
      const currentRentFormattedValue = formatByUom(
        Array.isArray(currentRentValue) ? currentRentValue[0] : currentRentValue,
        options.symbols,
        true
      );
      const askingRentValue = mapAndFormatDynamicValue(record, askingRent);
      const askingRentFormattedValue = formatByUom(
        Array.isArray(askingRentValue) ? askingRentValue[0] : askingRentValue,
        options.symbols,
        true
      );
      const rentUom = digger(record.data, 'ref_asking_rent_yearly_per_area_at_sale_uom_desc');
      const uomSymbols = !!rentUom ? rentUom.split('/') : null;
      const currencySymbol = !!uomSymbols && Array.isArray(uomSymbols) ? uomSymbols[0] : '';
      const areaSymbol = !!uomSymbols && Array.isArray(uomSymbols) && uomSymbols[1] ? uomSymbols[1].toLowerCase() : '';
      const areaValue = !!areaSymbol && COMMON_UOM_MAP[areaSymbol] ? COMMON_UOM_MAP[areaSymbol] : areaSymbol;

      let result: string | string[] = defaultTxt;
      if (askingRentFormattedValue != null) {
        result = formatQuotingPrice(
          currentRentFormattedValue,
          `${currencySymbol}${askingRentFormattedValue}`,
          `per ${areaValue || defaultTxt}`
        );
      }
      return result;
    },
  },
  9: {
    // 9 - Quoting Price
    displayFn: (record: ReportRecord, options, quotingPrice, rentPerSqft) => {
      const dynamicQuotingPrice = mapAndFormatDynamicValue(record, { ...quotingPrice, dataFormat: '.0-0' });
      const quotingPriceVal = formatByUom(
        Array.isArray(dynamicQuotingPrice) ? dynamicQuotingPrice[0] : dynamicQuotingPrice,
        options.symbols,
        true
      );
      const dynamicRentPerSqft = mapAndFormatDynamicValue(record, rentPerSqft);
      const rentPerSqftVal = formatByUom(
        Array.isArray(dynamicRentPerSqft) ? dynamicRentPerSqft[0] : dynamicRentPerSqft,
        options.symbols,
        true
      );
      let totalGrossAreaUom = record.associatedProperty && record.associatedProperty.total_gross_area_uom_desc;
      totalGrossAreaUom = totalGrossAreaUom && COMMON_UOM_MAP[areaUomAbbrev(totalGrossAreaUom)];

      let result: string | string[] = defaultTxt;
      if (rentPerSqftVal != null) {
        result = formatQuotingPrice(quotingPriceVal, rentPerSqftVal, `per ${totalGrossAreaUom || defaultTxt}`);
      }
      return result;
    },
  },
  10: {
    // 10 - Vendor
    displayFn: (record: ReportRecord, _, vendor) => {
      const vendorVal = mapAndFormatDynamicValue(record, vendor);
      return Array.isArray(vendorVal) ? concatValuesBy(', ', ...vendorVal) : vendorVal;
    },
  },
  18: {
    // 18 - Quoting Price NIY %
    displayFn: (record: ReportRecord) => {
      return getYieldPercentageByYieldType(record, YIELD_DETAILS.quoteInitialYield);
    },
  },
  11: {
    displayFn: (record: ReportRecord, _, arg) => {
      const purchasers = mapAndFormatDynamicValue(record, arg);
      return Array.isArray(purchasers) ? concatValuesBy(', ', ...purchasers) : purchasers;
    },
  }, // 11 - Purchaser
  12: {
    // 12 - Sale Price
    displayFn: (record: ReportRecord, options, salePrice, transactionValue) => {
      const arg = { ...salePrice, dataFormat: '.0-0' };
      const dynamicValue = mapAndFormatDynamicValue(record, arg);
      const salePriceValue = formatByUom(
        Array.isArray(dynamicValue) ? dynamicValue[0] : dynamicValue,
        options.symbols,
        true
      );
      const transValuePerSqft = mapAndFormatDynamicValue(record, transactionValue, null, false);
      const transValuePerSqftUOM = digger(record.data, 'reported_sale_price_per_area_value_uom');
      const uomSymbols = !!transValuePerSqftUOM ? transValuePerSqftUOM.split('/') : null;
      const currencySymbol = !!uomSymbols && Array.isArray(uomSymbols) ? uomSymbols[0] : '';
      const areaSymbol = !!uomSymbols && Array.isArray(uomSymbols) ? uomSymbols[1].toLowerCase() : '';
      const areaValue = !!areaSymbol && COMMON_UOM_MAP[areaSymbol] ? COMMON_UOM_MAP[areaSymbol] : areaSymbol;

      let result: string | string[] = defaultTxt;
      if (salePriceValue != null) {
        result =
          transValuePerSqft != null
            ? formatQuotingPrice(
                salePriceValue,
                `${currencySymbol}${transValuePerSqft}`,
                `per ${areaValue || defaultTxt}`
              )
            : salePriceValue;
      }
      return result;
    },
  },
  15: {
    // 15 - Status Date
    displayFn: (record: ReportRecord, _, date) => {
      const dateValue = mapAndFormatDynamicValue(record, date);
      const dateDisplayFormat = UserSettings.getGlobalDateDisplayFormat('value');

      const momentVal = moment(dateValue, dateDisplayFormat);
      return momentVal.isValid() ? momentVal.format('MMMM YYYY') : defaultTxt;
    },
  },
  171: { displayFn: displayFnWrapper }, // 171 - Date to Markets
  13: {
    // 13 - Sale Price NIY %
    displayFn: (record: ReportRecord) => {
      return getYieldPercentageByYieldType(record, YIELD_DETAILS.initialYield);
    },
  },
  296: { displayFn: displayFnWrapper }, // 296 - comments
};
